// "Standardwerte"-Gruppe der Settings-Navigation: Standard-Ansicht,
// Standard-Filter „Meine Aufgaben", Standard-Liste für neue Aufgaben,
// Sichtbare Abteilungen, Sprache. Ausgelagert aus routes/Settings.tsx
// (Redesign: 15 Abschnitte in Tabs gruppiert) nur um die Hauptdatei unter
// der 500-Zeilen-Grenze zu halten — wortgleiche Verschiebung, Logik bleibt
// in Settings.tsx.

import type { ComponentChildren } from "preact";
import type { UserPreferences, TaskList, Department } from "@juuplan/shared";
import type { DefaultProjectView, DefaultTaskFilter } from "../../lib/userDefaults";
import { DensityButton } from "./SettingsControls";

export interface DefaultsGroupProps {
  defaultView: DefaultProjectView;
  onDefaultView: (next: DefaultProjectView) => void;
  defaultFilter: DefaultTaskFilter;
  onDefaultFilter: (next: DefaultTaskFilter) => void;
  prefs: UserPreferences | null;
  saving: boolean;
  selectableLists: TaskList[];
  firstPersonalList: TaskList | undefined;
  departments: { loading: boolean; items: Department[] };
  onUpdate: (patch: Partial<UserPreferences>) => void;
  loadingHint: ComponentChildren;
}

export function DefaultsGroup(props: DefaultsGroupProps): preact.JSX.Element {
  const { prefs } = props;
  return (
    <div class="flex flex-col gap-8">
      {/* Standard-View beim Projekt-Öffnen */}
      <section>
        <h2 class="text-sm font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-3">
          Standard-Ansicht
        </h2>
        <div class="flex flex-wrap max-w-full rounded border border-[color:var(--color-outline-variant)] overflow-hidden">
          {(["table", "timeline", "kanban", "calendar"] as DefaultProjectView[]).map((v) => (
            <DensityButton
              key={v}
              active={props.defaultView === v}
              onClick={() => props.onDefaultView(v)}
              label={
                v === "table"
                  ? "Tabelle"
                  : v === "timeline"
                    ? "Zeitleiste"
                    : v === "kanban"
                      ? "Kanban"
                      : "Kalender"
              }
            />
          ))}
        </div>
        <p class="mt-2 text-xs text-[color:var(--color-on-surface-variant)]">
          Beim Öffnen eines Projekts wird automatisch diese Sicht aktiviert.
        </p>
      </section>

      {/* Standard-Filter für „Meine Aufgaben" */}
      <section>
        <h2 class="text-sm font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-3">
          Standard-Filter „Meine Aufgaben"
        </h2>
        <div class="flex flex-wrap max-w-full rounded border border-[color:var(--color-outline-variant)] overflow-hidden">
          {(["all", "this-week", "overdue"] as DefaultTaskFilter[]).map((f) => (
            <DensityButton
              key={f}
              active={props.defaultFilter === f}
              onClick={() => props.onDefaultFilter(f)}
              label={f === "all" ? "Alle" : f === "this-week" ? "Diese Woche" : "Überfällig"}
            />
          ))}
        </div>
      </section>

      {!prefs ? (
        props.loadingHint
      ) : (
        <>
          {/* Standard-Liste für Aufgaben, die ohne Listen-Kontext entstehen
              (Quick-Add unter „Alle Aufgaben"). Ohne Auswahl greift die erste
              persönliche Liste. */}
          <section>
            <h2 class="text-sm font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-3">
              Standard-Liste für neue Aufgaben
            </h2>
            {props.selectableLists.length === 0 ? (
              <p
                class="text-xs text-[color:var(--color-error)]"
                data-testid="settings-no-lists-hint"
              >
                Du hast noch keine Liste. Lege in der Seitenleiste unter „Listen" eine Liste an –
                danach kannst du sie hier als Standard setzen.
              </p>
            ) : (
              <>
                <select
                  id="settings-default-list"
                  aria-label="Standard-Liste für neue Aufgaben"
                  value={prefs.default_list_id !== null ? String(prefs.default_list_id) : ""}
                  disabled={props.saving}
                  onChange={(e) => {
                    const v = (e.target as HTMLSelectElement).value;
                    props.onUpdate({ default_list_id: v ? Number(v) : null });
                  }}
                  class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-2 text-base sm:text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] disabled:opacity-60"
                >
                  <option value="">
                    {props.firstPersonalList
                      ? `Automatisch – erste persönliche Liste („${props.firstPersonalList.name}“)`
                      : "Automatisch – erste persönliche Liste"}
                  </option>
                  {props.selectableLists.map((l) => (
                    <option key={l.id} value={String(l.id)}>
                      {l.scope === "space" ? `${l.space_name ?? "Space"} · ${l.name}` : l.name}
                    </option>
                  ))}
                </select>
                <p class="mt-2 text-xs text-[color:var(--color-on-surface-variant)]">
                  Aufgaben, die du unter „Alle Aufgaben" hinzufügst, landen in dieser Liste.
                  Ohne Auswahl wird die erste persönliche Liste verwendet.
                </p>
              </>
            )}
          </section>

          <section>
            <h2 class="text-sm font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-3">
              Sichtbare Abteilungen
            </h2>
            <p class="text-xs text-[color:var(--color-on-surface-variant)] mb-3">
              Wähle die Abteilungen deren Projekte du in der Sidebar und der
              Projekt-Übersicht sehen möchtest. Alle abgewählt = nichts wird
              ausgeblendet (sieht weiterhin alles).
            </p>
            <div class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] divide-y divide-[color:var(--color-outline-variant)]">
              {props.departments.loading ? (
                <p class="p-3 text-xs text-[color:var(--color-on-surface-variant)]">Lädt…</p>
              ) : props.departments.items.length === 0 ? (
                <p class="p-3 text-xs text-[color:var(--color-on-surface-variant)] italic">
                  Noch keine Abteilungen angelegt.
                </p>
              ) : (
                props.departments.items.map((d) => {
                  const visible = prefs.visible_departments;
                  const isAll = visible.length === 0;
                  const checked = isAll || visible.includes(d.id);
                  return (
                    <label
                      key={d.id}
                      class="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-[color:var(--color-surface-container-low)] text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const want = (e.target as HTMLInputElement).checked;
                          const base = isAll
                            ? props.departments.items.map((x) => x.id)
                            : visible.slice();
                          const next = want
                            ? Array.from(new Set([...base, d.id]))
                            : base.filter((x) => x !== d.id);
                          props.onUpdate({ visible_departments: next });
                        }}
                        class="accent-[color:var(--color-primary)]"
                      />
                      <span class="flex-1 text-[color:var(--color-on-surface)]">{d.name}</span>
                      {d.is_system && (
                        <span class="text-label uppercase tracking-wider text-[color:var(--color-primary)]">
                          System
                        </span>
                      )}
                    </label>
                  );
                })
              )}
            </div>
          </section>

          <section>
            <h2 class="text-sm font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-3">
              Sprache
            </h2>
            <p class="text-xs text-[color:var(--color-on-surface-variant)] mb-3">
              Sprache der KI-Antworten (Zusammenfassungen und Vorschläge).
            </p>
            <select
              value={prefs.language}
              onChange={(e) =>
                props.onUpdate({ language: (e.target as HTMLSelectElement).value as "de" | "en" })
              }
              disabled={props.saving}
              class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] text-sm text-[color:var(--color-on-surface)] px-3 py-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
            >
              <option value="de">Deutsch</option>
              <option value="en">English</option>
            </select>
          </section>
        </>
      )}
    </div>
  );
}
